import { useCallback, useEffect } from 'react';
import { UseFormGetValues, UseFormWatch } from 'react-hook-form';

import { useServerStorage } from 'utils/useServerStorage/useServerStorage';

import type { EmailDraftV2, FormValues } from '../composeEmailModal.types';
import { convertFormValuesToEmailDraftV2 } from '../composeEmailModal.utils';

type UseEmailDraftOptions = {
  draft?: EmailDraftV2;
  getValues: UseFormGetValues<FormValues>;
  watch: UseFormWatch<FormValues>;
  hide: () => void;
};

type UseEmailDraftResult = {
  draftTimestamp: number | null;
  lastUsedEmail: string | undefined;
  handleSaveDraft: () => void;
  deleteDraft: () => void;
};

export function useEmailDraft({
  draft,
  getValues,
  watch,
  hide,
}: UseEmailDraftOptions): UseEmailDraftResult {
  const { data: userStorage, set: setUserStorage } = useServerStorage('user');
  const lastUsedEmail = userStorage?.global?.lastUsedEmailIntegration;
  const draftTimestamp = draft?.timestamp ?? null;
  const fromEmail = watch('fromEmail');

  useEffect(() => {
    const email = fromEmail?.[0];
    if (!email || email === lastUsedEmail) return;
    setUserStorage({
      global: (draft) => {
        draft.lastUsedEmailIntegration = email;
      },
    });
  }, [fromEmail, lastUsedEmail, setUserStorage]);

  const handleSaveDraft = useCallback(() => {
    const values = getValues();
    const timestamp = draftTimestamp ?? Date.now();
    setUserStorage({
      drafts: (storage) => {
        const items = (storage.email ??= []);
        type DraftItem = (typeof items)[0];
        const newDraft = convertFormValuesToEmailDraftV2(
          values,
          timestamp,
        ) as unknown as DraftItem;
        const existingIdx = items.findIndex((d) => d.timestamp === timestamp);
        if (existingIdx !== -1) {
          items[existingIdx] = newDraft;
        } else {
          items.push(newDraft);
        }
      },
    });
    hide();
  }, [draftTimestamp, getValues, setUserStorage, hide]);

  const deleteDraft = useCallback(() => {
    if (draftTimestamp === null) return;
    setUserStorage({
      drafts: (storage) => {
        if (!storage.email) return;
        const idx = storage.email.findIndex(
          (d) => d.timestamp === draftTimestamp,
        );
        if (idx !== -1) storage.email.splice(idx, 1);
      },
    });
  }, [draftTimestamp, setUserStorage]);

  return { draftTimestamp, lastUsedEmail, handleSaveDraft, deleteDraft };
}
